import { Grid, GridItem, useDisclosure } from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { WidgetRegistry } from "./dashboardConfig";
import {
  DashboardConfig,
  WidgetConfig,
} from "../../shared/v2/definitions/dashboards";
import { Filters, UserInfo } from "./widgets/BaseWidgetProps";
import SystemSearchWidget from "./widgets/SystemSearchWidget";
import { useMinimizedWidgets } from "./useMinimizedWidgets";
import { useAppState } from "../App/AppProvider";
import { DashboardSettings } from "../../utils/settings/DashboardSettings";

type Props = {
  dashboardConfig: DashboardConfig;
  users?: UserInfo[];
};

export default function DashboardRenderer({ dashboardConfig, users }: Props) {
  const searchResultsState = useDisclosure({ defaultIsOpen: false });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFilters, setSearchFilters] = useState<Filters | null>(null);

  const { minimized, toggleWidget } = useMinimizedWidgets();
  const [sortedWidgets, setSortedWidgets] = useState<WidgetConfig[]>([]);

  const { permissions } = useAppState().app;

  const handleChangeSearch = (value: string) => {
    setSearchQuery(value);
    if (!searchResultsState.isOpen) searchResultsState.onOpen();
  };

  const handleChangeFilter = (filters: Filters) => {
    setSearchFilters(filters);
    if (!searchResultsState.isOpen) searchResultsState.onOpen();
  };

  const handleToggleMinimize = (widgetID: string) => {
    toggleWidget(widgetID);
  };

  const renderWidget = (widget: WidgetConfig) => {
    if (widget.type === "system-search") {
      return (
        <SystemSearchWidget
          filters={{ searchQuery, searchFilters }}
          setFilters={handleChangeFilter}
          users={users}
          onChangeSearch={handleChangeSearch}
          disclosureState={searchResultsState}
        />
      );
    }

    const Widget = WidgetRegistry[widget.type];

    const isMinimized = DashboardSettings.getGetStartedMinimized();
    // we want only the "super users" to see the system checklist
    if (widget.type === "system-checklist" && !permissions.ACCOUNT_SETTINGS) {
      return null;
    }

    return (
      <Widget
        users={users}
        title={widget.title}
        widgetID={widget.widgetID}
        dashboardID={dashboardConfig.dashboardID}
        setFilters={handleChangeFilter}
        config={widget.config as any}
        isMinimized={isMinimized}
        toggleMinimize={() => handleToggleMinimize(widget.widgetID)}
      />
    );
  };

  useEffect(() => {
    const sorted = [...dashboardConfig.widgets].sort((a, b) => {
      const aMin = minimized.includes(a.widgetID);
      const bMin = minimized.includes(b.widgetID);
      return aMin === bMin ? 0 : aMin ? 1 : -1;
    });
    setSortedWidgets(sorted);
  }, [minimized, dashboardConfig.widgets]);

  return (
    <Grid w="full" h="full" templateColumns="repeat(3, 1fr)" gap="2">
      {sortedWidgets.map((widget) => (
        <GridItem key={widget.widgetID} colSpan={widget.span || 1}>
          {renderWidget(widget)}
        </GridItem>
      ))}
    </Grid>
  );
}
